import type { PileTypeId, ProjectId, UnitTypeId } from '../shared/ids';
import {
  CAMPO_CODIGO_DE_COMPRAS,
  LINHAS_ATE_O_CABECALHO,
  reconhecerColunas,
  simplificarTitulo,
  type CamposDoCabecalho,
} from '../imports/cabecalho';
import { normalizeName } from '../shared/naming';
import type { AppliesTo } from './applies-to';
import type { MeasureBy } from './geometry';
import { checkPileTypeFields, type PileSteel, type PileType } from './pile';
import {
  emptyCatalog,
  guessItemKind,
  itemDescription,
  serviceKey,
  type CatalogDiscipline,
  type CatalogItem,
  type CatalogService,
  type ServiceCatalog,
} from './service-catalog';
import { parseNumber } from './service-sheet';
import {
  isSteelPurchaseService,
  normalizeSteelRates,
  parseSteelGauge,
  STEEL_GAUGES,
  STEEL_PURCHASE_SERVICE,
  steelGaugeLabel,
  type SteelGauge,
  type SteelRate,
} from './steel';
import { ITEM_KIND_LABEL, type ItemKind } from './takeoff-line';

/**
 * A planilha **plana** de índices e taxas de aço (Fase 27, D17, D18 e ADR-177).
 *
 * `service-sheet.ts` lê o orçamento da construtora, onde a hierarquia está na numeração do
 * item (3, 3.1, 3.1.1). Esta é a outra planilha: a que o **sistema exporta** para o usuário
 * preencher e importar de volta. Uma linha por serviço × item, as três pontas escritas em
 * colunas — Disciplina, Serviço, Item —, e é essa tríade no cabeçalho que a distingue da
 * numerada (RN-374). Sem coluna de numeração, sem cabeçalho de dois níveis: planilha que o
 * usuário monta a mão tem de entrar como ele a escreveu.
 *
 * Quatro abas são lidas:
 *
 * - **Índices**: o consumo de cada item por unidade do medido do serviço, e a perda;
 * - **Taxas de aço**: os kg de cada bitola por m³ de concreto, por disciplina (RN-367);
 * - **Tipos** (etapa 1b): o tipo de estaca — Tipo | Diâmetro | Comprimento | Bitola | kg por
 *   estaca —, uma linha por tipo × bitola, com o diâmetro e o comprimento repetidos nas linhas do
 *   mesmo tipo (D1, D17);
 * - **Estacas** (etapa 1b): Nome | Tipo | Pavimento, este último opcional (F10).
 *
 * Qualquer outra aba é ignorada **e dita**: ficar calado faria o usuário esperar o que a aba
 * "Observações" não traz.
 *
 * O diâmetro do tipo é lido em **centímetro ou metro**, e a regra é o tamanho: acima de 3 é
 * centímetro e vira metro (40 → 0,40), até 3 já é metro (0,40 → 0,40). Estaca de fundação não
 * passa de dois metros de diâmetro, então os dois intervalos nunca se confundem. O comprimento é
 * sempre em metro.
 *
 * ## O que o import não faz
 *
 * Mescla, **nunca remove**. O item que não está na planilha continua no serviço, e o que a
 * planilha não diz — célula de índice vazia — não apaga o que já estava lá. Também não cria
 * disciplina nem serviço: eles são cadastro da obra, e a planilha que escreve um nome torto
 * recebe um aviso, e não um serviço novo que ninguém pediu. O mesmo vale para os tipos de
 * estaca (a bitola que a planilha não cita continua no tipo) e para as estacas (a que já existe
 * no pavimento não é duplicada, RN-376).
 */

// ── O que a obra tem ────────────────────────────────────────────────────────────

/**
 * O serviço do cadastro da obra, na forma mínima que a planilha precisa conhecer.
 *
 * O catálogo do rascunho só tem os serviços que alguém já abriu no quantitativo; o cadastro
 * da obra (os dela mais os globais) tem todos. A planilha casa com o **cadastro**, e o
 * catálogo só ganha o serviço quando uma linha o toca — assim o import não despeja cento e
 * cinquenta serviços vazios no rascunho de quem preencheu duas disciplinas.
 */
export interface ServicoDisponivel {
  readonly disciplineName: string;
  readonly name: string;
  readonly measureBy: MeasureBy;
  readonly appliesTo?: AppliesTo;
  /** O nome da unidade, como o cadastro de unidades a escreve (`m²`, `kg`). */
  readonly unit?: string;
}

export interface ServicoListado {
  readonly name: string;
  readonly unit?: string;
  readonly measureBy: MeasureBy;
  readonly appliesTo?: AppliesTo;
  /** Os itens que o catálogo já tem — vazio para o serviço que só o cadastro conhece. */
  readonly items: readonly CatalogItem[];
}

export interface DisciplinaListada {
  readonly name: string;
  readonly steelRates: readonly SteelRate[];
  readonly services: readonly ServicoListado[];
}

/**
 * O catálogo e o cadastro da obra, juntos — disciplina → serviço, com os itens que há.
 *
 * É a lista sobre a qual a planilha casa nomes e a lista que o modelo exportado percorre: as
 * duas precisam concordar sobre **quais serviços existem**, ou o modelo trairia uma linha que
 * o import depois não reconhece.
 *
 * A ordem é a do catálogo, e o que só o cadastro tem vem depois. Na medida e na unidade
 * manda o cadastro, que é o que o servidor grava; os itens são do catálogo, o único lugar
 * onde existem.
 */
export function listarDisciplinas(
  catalogo: ServiceCatalog | undefined,
  disponiveis: readonly ServicoDisponivel[],
  desativados: readonly ServicoDesativado[] = [],
): readonly DisciplinaListada[] {
  interface Montando {
    readonly name: string;
    steelRates: readonly SteelRate[];
    readonly services: Map<string, ServicoListado>;
    /** Perdeu serviço para a lista de desativados — e, vazia, não é disciplina da obra. */
    seTornouVazia: boolean;
  }
  const disciplinas = new Map<string, Montando>();
  const dessa = (nome: string): Montando => {
    const chave = normalizeName(nome);
    let achada = disciplinas.get(chave);
    if (achada === undefined) {
      achada = { name: nome, steelRates: [], services: new Map(), seTornouVazia: false };
      disciplinas.set(chave, achada);
    }
    return achada;
  };
  const fora = new Set(
    desativados.map((servico) => serviceKey(servico.disciplineName, servico.name)),
  );

  for (const disciplina of catalogo?.disciplines ?? []) {
    const alvo = dessa(disciplina.name);
    alvo.steelRates = disciplina.steelRates ?? [];
    for (const servico of disciplina.services) {
      // O serviço desativado não é mais do cadastro: o catálogo do rascunho ainda o guarda (ele
      // nasce do retrato dos itens), mas o modelo não o oferece e o import não o casa.
      if (fora.has(serviceKey(disciplina.name, servico.name))) {
        alvo.seTornouVazia = true;
        continue;
      }
      alvo.services.set(normalizeName(servico.name), {
        name: servico.name,
        measureBy: servico.measureBy,
        items: servico.items,
        ...(servico.appliesTo === undefined ? {} : { appliesTo: servico.appliesTo }),
        ...(servico.unit === undefined ? {} : { unit: servico.unit }),
      });
    }
  }

  for (const servico of disponiveis) {
    if (fora.has(serviceKey(servico.disciplineName, servico.name))) continue;
    const alvo = dessa(servico.disciplineName);
    const chave = normalizeName(servico.name);
    const doCatalogo = alvo.services.get(chave);
    const unidade = servico.unit ?? doCatalogo?.unit;
    alvo.services.set(chave, {
      name: doCatalogo?.name ?? servico.name,
      measureBy: servico.measureBy,
      items: doCatalogo?.items ?? [],
      // O elemento segue a medida, e o cadastro manda nos dois: manter o do catálogo
      // quando o cadastro diz "pendente" juntaria uma medida nova a uma peça velha.
      ...(servico.appliesTo === undefined ? {} : { appliesTo: servico.appliesTo }),
      ...(unidade === undefined ? {} : { unit: unidade }),
    });
  }

  return [...disciplinas.values()]
    .filter((disciplina) => !(disciplina.seTornouVazia && disciplina.services.size === 0))
    .map((disciplina) => ({
      name: disciplina.name,
      steelRates: disciplina.steelRates,
      services: [...disciplina.services.values()],
    }));
}

/**
 * Um serviço que a obra desativou (`isActive = false`), pelo nome dele e o da disciplina.
 *
 * O cadastro da obra não o oferece mais — e a planilha também não: o modelo não o lista, e o
 * import avisa em vez de criar item nele. Vem de fora porque o catálogo do rascunho não sabe
 * se o serviço está ativo.
 */
export interface ServicoDesativado {
  readonly disciplineName: string;
  readonly name: string;
}

/**
 * O catálogo sem os serviços desativados — e sem a disciplina que ficou sem nenhum.
 *
 * É o catálogo que a tela **conta**: "N disciplinas · M serviços" diz o que a obra usa, e não o
 * que o cadastro velho ainda guarda.
 */
export function semServicosDesativados(
  catalogo: ServiceCatalog,
  desativados: readonly ServicoDesativado[],
): ServiceCatalog {
  if (desativados.length === 0) return catalogo;
  const fora = new Set(
    desativados.map((servico) => serviceKey(servico.disciplineName, servico.name)),
  );
  return {
    ...catalogo,
    disciplines: catalogo.disciplines.flatMap((disciplina) => {
      const servicos = disciplina.services.filter(
        (servico) => !fora.has(serviceKey(disciplina.name, servico.name)),
      );
      return servicos.length === 0 && disciplina.services.length > 0
        ? []
        : [{ ...disciplina, services: servicos }];
    }),
  };
}

// ── Os cabeçalhos ───────────────────────────────────────────────────────────────

/**
 * O texto de um cabeçalho, para comparar: sem acento, sem caixa, sem pontuação.
 *
 * `NFKD` e não `NFD`: `kg/m³` precisa virar `kg m3`, e o expoente é caractere de
 * compatibilidade que só a forma compatível desmonta. Quem escreve `Serviço`, `SERVICO` e
 * `Servico` escreveu a mesma coluna.
 */
const simplificar = simplificarTitulo;

/**
 * Cada coluna e os nomes com que a planilha a escreve, já simplificados.
 *
 * Só `disciplina`, `servico` e `item` são obrigatórias: com as três a aba é de índices, e o
 * resto é informação que o modelo traz para quem preenche (a etapa, a unidade do serviço, o
 * "mede por") ou dado que ele pode dar (código, tipo, unidade, índice, perda).
 */
export const CABECALHO_INDICES = {
  etapa: ['etapa'],
  disciplina: ['disciplina'],
  servico: ['servico', 'atividade'],
  unidadeDoServico: [
    'unidade do servico',
    'und do servico',
    'unid do servico',
    'un do servico',
    'unidade servico',
  ],
  medePor: ['mede por', 'medido por'],
  item: ['item', 'insumo', 'nome do item', 'descricao do item'],
  codigo: CAMPO_CODIGO_DE_COMPRAS,
  tipo: ['tipo', 'classe', 'tipo do item', 'classe do item'],
  unidadeDoItem: [
    'unidade do item',
    'und do item',
    'unid do item',
    'un do item',
    'unidade',
    'und',
    'unid',
  ],
  indice: ['indice', 'indice de consumo', 'consumo'],
  perda: ['perda', 'perda percentual', 'perdas'],
} as const;

const CABECALHO_TAXAS = {
  disciplina: ['disciplina'],
  bitola: ['bitola', 'bitola de aco', 'aco', 'diametro'],
  taxa: [
    'kg m3',
    'kg por m3',
    'taxa',
    'taxa de aco',
    'taxa kg m3',
    'taxa kg por m3',
    'kg m3 de concreto',
  ],
} as const;

/**
 * A aba **Tipos**: só `tipo`, `diametro` e `comprimento` são obrigatórias. Sem as colunas de
 * aço o tipo ainda é lido — uma estaca sem armadura é um tipo válido.
 */
const CABECALHO_TIPOS = {
  tipo: ['tipo', 'tipo de estaca', 'nome do tipo'],
  diametro: ['diametro', 'diametro cm', 'diametro m', 'diametro da estaca'],
  comprimento: ['comprimento', 'comprimento m', 'comprimento da estaca'],
  bitola: ['bitola', 'bitola de aco', 'aco'],
  kgPorEstaca: ['kg por estaca', 'kg estaca', 'kg de aco por estaca', 'aco por estaca', 'kg'],
} as const;

/** A aba **Estacas**: `nome` e `tipo` bastam; o pavimento é opcional (F10). */
const CABECALHO_ESTACAS = {
  nome: ['nome', 'estaca', 'nome da estaca'],
  tipo: ['tipo', 'tipo de estaca', 'tipo da estaca'],
  pavimento: ['pavimento', 'tipologia', 'pavimento da estaca'],
} as const;

type Colunas<T extends CamposDoCabecalho<string>> = { readonly [K in keyof T]: number };

/**
 * Que coluna é cada campo, pelo reconhecedor comum (`imports/cabecalho.ts`): em qualquer
 * posição, e com as variações de título — "Cód. compras", "Qtd.", "Unidade do item".
 */
function mapear<T extends CamposDoCabecalho<string>>(
  cabecalho: readonly string[],
  sinonimos: T,
): Colunas<T> {
  return reconhecerColunas(sinonimos, cabecalho) as Colunas<T>;
}

type CabecalhoAchado =
  | {
      readonly tipo: 'INDICES';
      readonly linha: number;
      readonly colunas: Colunas<typeof CABECALHO_INDICES>;
    }
  | {
      readonly tipo: 'TAXAS';
      readonly linha: number;
      readonly colunas: Colunas<typeof CABECALHO_TAXAS>;
    }
  | {
      readonly tipo: 'TIPOS';
      readonly linha: number;
      readonly colunas: Colunas<typeof CABECALHO_TIPOS>;
    }
  | {
      readonly tipo: 'ESTACAS';
      readonly linha: number;
      readonly colunas: Colunas<typeof CABECALHO_ESTACAS>;
    };

/** Título e linha em branco antes do cabeçalho são normais. */
const LINHAS_PROCURADAS = LINHAS_ATE_O_CABECALHO;

function acharCabecalho(rows: readonly (readonly string[])[]): CabecalhoAchado | undefined {
  for (let linha = 0; linha < Math.min(rows.length, LINHAS_PROCURADAS); linha += 1) {
    const row = rows[linha]!;
    const indices = mapear(row, CABECALHO_INDICES);
    if (indices.disciplina >= 0 && indices.servico >= 0 && indices.item >= 0) {
      return { tipo: 'INDICES', linha, colunas: indices };
    }
    const taxas = mapear(row, CABECALHO_TAXAS);
    if (taxas.disciplina >= 0 && taxas.bitola >= 0 && taxas.taxa >= 0) {
      return { tipo: 'TAXAS', linha, colunas: taxas };
    }
    const tipos = mapear(row, CABECALHO_TIPOS);
    if (tipos.tipo >= 0 && tipos.diametro >= 0 && tipos.comprimento >= 0) {
      return { tipo: 'TIPOS', linha, colunas: tipos };
    }
    const estacas = mapear(row, CABECALHO_ESTACAS);
    if (estacas.nome >= 0 && estacas.tipo >= 0) {
      return { tipo: 'ESTACAS', linha, colunas: estacas };
    }
  }
  return undefined;
}

export interface AbaDaPlanilha {
  readonly name: string;
  readonly rows: readonly (readonly string[])[];
}

/**
 * Alguma aba tem o cabeçalho da planilha plana? (RN-374)
 *
 * É o que decide **qual** leitura o import faz: a planilha numerada do orçamento continua
 * entrando como está, e a plana entra pelo mesmo botão. Basta uma aba — o modelo exportado
 * tem quatro, mas quem monta a sua pode trazer só as taxas, ou só as estacas.
 */
export function ehPlanilhaPlana(abas: readonly AbaDaPlanilha[]): boolean {
  return abas.some((aba) => acharCabecalho(aba.rows) !== undefined);
}

// ── A leitura ───────────────────────────────────────────────────────────────────

/**
 * Um aviso, com o lugar onde ele aconteceu.
 *
 * A linha é a **posição na aba lida**, contando o cabeçalho: o leitor de XLSX descarta as
 * linhas em branco, então numa pasta com buraco ela pode ficar abaixo da que o Excel mostra.
 * No modelo que o sistema exporta, que não tem buraco, as duas coincidem.
 */
export interface AvisoDaPlanilha {
  readonly aba: string;
  readonly linha?: number;
  readonly mensagem: string;
}

export type TipoDeAba = 'INDICES' | 'TAXAS' | 'TIPOS' | 'ESTACAS' | 'IGNORADA';

export interface AbaLida {
  readonly name: string;
  readonly tipo: TipoDeAba;
  /** Linhas que viraram dado — as que a leitura aproveitou, e não as que avisou. */
  readonly linhas: number;
}

export interface LinhaDeIndice {
  readonly aba: string;
  readonly linha: number;
  /** A etapa, como a planilha a escreve. Informação para quem preenche: o import não a usa. */
  readonly etapa?: string;
  readonly disciplina: string;
  readonly servico: string;
  /** Idem: o serviço já tem unidade no cadastro, e a planilha não a muda. */
  readonly unidadeDoServico?: string;
  /** Idem: diz em que base o índice foi pensado (m³, m², kg), e não a muda. */
  readonly medePor?: string;
  readonly item: string;
  readonly codigo?: string;
  readonly tipo?: ItemKind;
  readonly unidadeDoItem?: string;
  /**
   * O consumo por unidade do medido. **Ausente quando a célula está vazia ou é 0**: zero é
   * "não preenchido" desde a Fase 24 (D14) e não "consome nada" — a planilha não tem um
   * jeito de dizer a diferença, e o sistema não inventa um.
   */
  readonly indice?: number;
  /** A perda em por cento. Ausente = célula vazia. Zero é uma perda zero declarada. */
  readonly perda?: number;
}

export interface LinhaDeTaxa {
  readonly aba: string;
  readonly linha: number;
  readonly disciplina: string;
  readonly gauge: SteelGauge;
  readonly kgPerM3: number;
}

/**
 * Uma linha da aba **Tipos**: o tipo, e uma bitola dele.
 *
 * Diâmetro e comprimento já em **metro** — a conversão do centímetro é da leitura. Todos os
 * campos além do nome são opcionais porque a linha pode não os trazer (a segunda bitola do
 * tipo costuma repetir o diâmetro, mas quem deixou em branco não errou); quem decide se o que
 * sobrou basta para criar o tipo é a mescla.
 */
export interface LinhaDeTipoDeEstaca {
  readonly aba: string;
  readonly linha: number;
  readonly nome: string;
  readonly diametro?: number;
  readonly comprimento?: number;
  readonly aco?: PileSteel;
}

/** Uma linha da aba **Estacas**: o nome, o tipo pelo nome e, se escrito, o pavimento. */
export interface LinhaDeEstaca {
  readonly aba: string;
  readonly linha: number;
  readonly nome: string;
  /** O nome do tipo, como a planilha o escreve. */
  readonly tipo: string;
  /** O nome da tipologia ou do pavimento. Vence a escolha da tela, linha a linha (F10). */
  readonly pavimento?: string;
}

export interface LeituraDaPlanilha {
  readonly abas: readonly AbaLida[];
  readonly indices: readonly LinhaDeIndice[];
  readonly taxas: readonly LinhaDeTaxa[];
  readonly tiposDeEstaca: readonly LinhaDeTipoDeEstaca[];
  readonly estacas: readonly LinhaDeEstaca[];
  readonly avisos: readonly AvisoDaPlanilha[];
}

/**
 * Os tetos do banco — o mesmo número que a tela do índice recusa (`SET_ITEM_INDEX`).
 *
 * Recusar aqui é dizer na linha da planilha; deixar passar faria o servidor recusar a obra
 * inteira, sem dizer qual linha.
 */
const TETO_DO_INDICE = 99_999_999;
const TETO_DA_PERDA = 999.999;

/**
 * As casas que o banco guarda: `numeric(14, 6)` no índice e `numeric(7, 3)` na perda.
 *
 * O domínio arredonda a essas casas ao gravar (`service-items.ts`). A leitura da planilha faz o
 * mesmo **antes** de comparar com a obra, e avisa quando corta: comparar o número escrito com o
 * gravado diria "índice atualizado" a cada reimportação do mesmo arquivo, e o corte calado
 * deixaria quem escreveu 0,1234567 achando que ele entrou inteiro.
 */
const CASAS_DO_INDICE = 6;
const CASAS_DA_PERDA = 3;

function arredondar(valor: number, casas: number): number {
  const fator = 10 ** casas;
  return Math.round(valor * fator) / fator;
}

const TIPOS_DE_ITEM: ReadonlyMap<string, ItemKind> = new Map<string, ItemKind>([
  ...(Object.entries(ITEM_KIND_LABEL) as [ItemKind, string][]).map(
    ([tipo, rotulo]): [string, ItemKind] => [simplificar(rotulo), tipo],
  ),
  // Como a obra abrevia: "M.O." é como a planilha de orçamento escreve mão de obra.
  ['mo', 'MAO_DE_OBRA'],
]);

function celula(row: readonly string[], coluna: number): string {
  return coluna < 0 ? '' : (row[coluna] ?? '').trim();
}

/** Vazio para o que a célula não tem: o campo ausente é mais claro que a string vazia. */
function opcional<K extends string>(campo: K, valor: string): { [P in K]?: string } {
  return valor === '' ? {} : ({ [campo]: valor } as { [P in K]: string });
}

const ROTULOS_DAS_BITOLAS = STEEL_GAUGES.map((bitola) => bitola.label).join(', ');

/**
 * Acima disto o diâmetro está em centímetro; até aqui, em metro.
 *
 * Três metros de diâmetro é mais que qualquer estaca de fundação, e três centímetros é menos que
 * qualquer uma: o número que a planilha escreve só cabe em uma das duas leituras.
 */
const LIMITE_DO_DIAMETRO_EM_METRO = 3;

/** Um número positivo da planilha, ou o aviso de por que não é. */
function medidaDoTipo(
  escrito: string,
  rotulo: string,
  tipo: string,
  linha: number,
  avisar: (linha: number, mensagem: string) => void,
): number | undefined {
  if (escrito === '') return undefined;
  const numero = parseNumber(escrito);
  if (numero === undefined || numero <= 0) {
    avisar(linha, `O ${rotulo} "${escrito}" do tipo ${tipo} não é um número maior que zero.`);
    return undefined;
  }
  return numero;
}

/**
 * Uma linha da aba Tipos. Devolve `undefined` para a linha que não é de tipo nenhum — a
 * vazia passa calada, e a que tem dado mas não tem nome é avisada.
 */
function lerLinhaDeTipo(
  aba: string,
  linha: number,
  row: readonly string[],
  c: Colunas<typeof CABECALHO_TIPOS>,
  avisar: (linha: number, mensagem: string) => void,
): LinhaDeTipoDeEstaca | undefined {
  const nome = celula(row, c.tipo);
  const diametroEscrito = celula(row, c.diametro);
  const comprimentoEscrito = celula(row, c.comprimento);
  const bitolaEscrita = celula(row, c.bitola);
  const kgEscrito = celula(row, c.kgPorEstaca);

  if (nome === '') {
    if ([diametroEscrito, comprimentoEscrito, bitolaEscrita, kgEscrito].some((v) => v !== '')) {
      avisar(linha, 'A linha tem diâmetro, comprimento ou aço, mas não tem o nome do tipo.');
    }
    return undefined;
  }

  const escrito = medidaDoTipo(diametroEscrito, 'diâmetro', nome, linha, avisar);
  const diametro =
    escrito === undefined
      ? undefined
      : escrito > LIMITE_DO_DIAMETRO_EM_METRO
        ? escrito / 100
        : escrito;
  const comprimento = medidaDoTipo(comprimentoEscrito, 'comprimento', nome, linha, avisar);

  // Como na aba de taxas: a linha do modelo que ninguém preencheu (uma por bitola, a maioria
  // vazia) e o zero — "esta bitola não vai neste tipo" — passam calados.
  let aco: PileSteel | undefined;
  if (kgEscrito !== '') {
    const kg = parseNumber(kgEscrito);
    if (kg === undefined) {
      avisar(
        linha,
        `Os kg "${kgEscrito}" de aço do tipo ${nome} não são um número: o tipo entra sem esse aço.`,
      );
    } else if (kg < 0) {
      avisar(
        linha,
        `Os kg "${kgEscrito}" de aço do tipo ${nome} não podem ser negativos: o tipo entra sem esse aço.`,
      );
    } else if (kg > 0) {
      const gauge = bitolaEscrita === '' ? undefined : parseSteelGauge(bitolaEscrita);
      if (gauge === undefined) {
        avisar(
          linha,
          bitolaEscrita === ''
            ? `O aço do tipo ${nome} está sem bitola: o tipo entra sem esse aço. Use: ${ROTULOS_DAS_BITOLAS}.`
            : `A bitola "${bitolaEscrita}" do tipo ${nome} não é reconhecida: o tipo entra sem esse aço. Use: ${ROTULOS_DAS_BITOLAS}.`,
        );
      } else {
        aco = { gauge, kgPerPile: kg };
      }
    }
  }

  return {
    aba,
    linha,
    nome,
    ...(diametro === undefined ? {} : { diametro }),
    ...(comprimento === undefined ? {} : { comprimento }),
    ...(aco === undefined ? {} : { aco }),
  };
}

/** Uma linha da aba Estacas. A estaca sem tipo é avisada aqui: o tipo é obrigatório (RN-362). */
function lerLinhaDeEstaca(
  aba: string,
  linha: number,
  row: readonly string[],
  c: Colunas<typeof CABECALHO_ESTACAS>,
  avisar: (linha: number, mensagem: string) => void,
): LinhaDeEstaca | undefined {
  const nome = celula(row, c.nome);
  const tipo = celula(row, c.tipo);
  const pavimento = celula(row, c.pavimento);

  if (nome === '') {
    if (tipo !== '' || pavimento !== '') {
      avisar(linha, 'A linha tem tipo ou pavimento, mas não tem o nome da estaca.');
    }
    return undefined;
  }
  if (tipo === '') {
    avisar(linha, `A estaca ${nome} está sem tipo: é dele que sai o diâmetro.`);
    return undefined;
  }
  return { aba, linha, nome, tipo, ...opcional('pavimento', pavimento) };
}

/**
 * As abas da pasta, lidas como planilha plana.
 *
 * Nada aqui olha o catálogo: a leitura devolve o que a planilha **diz**, com os avisos do que
 * ela diz torto — número que não é número, tipo que não existe, linha sem disciplina. O que
 * depende da obra (o serviço existe? o item já está lá?) é de `mesclarNoCatalogo`.
 */
export function lerPlanilhaDeIndices(abas: readonly AbaDaPlanilha[]): LeituraDaPlanilha {
  const lidas: AbaLida[] = [];
  const indices: LinhaDeIndice[] = [];
  const taxas: LinhaDeTaxa[] = [];
  const tiposDeEstaca: LinhaDeTipoDeEstaca[] = [];
  const estacas: LinhaDeEstaca[] = [];
  const avisos: AvisoDaPlanilha[] = [];

  for (const aba of abas) {
    const cabecalho = acharCabecalho(aba.rows);
    if (cabecalho === undefined) {
      lidas.push({ name: aba.name, tipo: 'IGNORADA', linhas: 0 });
      avisos.push({
        aba: aba.name,
        mensagem: `A aba "${aba.name}" foi ignorada: o cabeçalho não é o de índices (Disciplina, Serviço, Item), o de taxas de aço (Disciplina, Bitola, kg/m³), o de tipos de estaca (Tipo, Diâmetro, Comprimento) nem o de estacas (Nome, Tipo).`,
      });
      continue;
    }

    const avisar = (linha: number, mensagem: string): void => {
      avisos.push({ aba: aba.name, linha, mensagem });
    };
    let aproveitadas = 0;

    for (let posicao = cabecalho.linha + 1; posicao < aba.rows.length; posicao += 1) {
      const row = aba.rows[posicao]!;
      const linha = posicao + 1;

      if (cabecalho.tipo === 'TIPOS') {
        const lida = lerLinhaDeTipo(aba.name, linha, row, cabecalho.colunas, avisar);
        if (lida === undefined) continue;
        tiposDeEstaca.push(lida);
        aproveitadas += 1;
        continue;
      }

      if (cabecalho.tipo === 'ESTACAS') {
        const lida = lerLinhaDeEstaca(aba.name, linha, row, cabecalho.colunas, avisar);
        if (lida === undefined) continue;
        estacas.push(lida);
        aproveitadas += 1;
        continue;
      }

      if (cabecalho.tipo === 'TAXAS') {
        const c = cabecalho.colunas;
        const disciplina = celula(row, c.disciplina);
        const bitola = celula(row, c.bitola);
        const taxa = celula(row, c.taxa);
        // A linha do modelo que ninguém preencheu: uma por bitola, e a maioria vazia.
        if (taxa === '') continue;

        const numero = parseNumber(taxa);
        if (numero === undefined) {
          avisar(linha, `A taxa "${taxa}" não é um número.`);
          continue;
        }
        // Zero não é taxa: a bitola que a disciplina não usa não tem linha (`steel.ts`). Quem
        // escreve 0 em nove bitolas quer dizer "não uso", e avisar nove vezes é ruído.
        if (numero === 0) continue;
        if (disciplina === '') {
          avisar(linha, 'A taxa está sem disciplina.');
          continue;
        }
        const gauge = parseSteelGauge(bitola);
        if (gauge === undefined) {
          avisar(
            linha,
            bitola === ''
              ? `A taxa de ${disciplina} está sem bitola. Use: ${ROTULOS_DAS_BITOLAS}.`
              : `A bitola "${bitola}" não é reconhecida. Use: ${ROTULOS_DAS_BITOLAS}.`,
          );
          continue;
        }
        taxas.push({ aba: aba.name, linha, disciplina, gauge, kgPerM3: numero });
        aproveitadas += 1;
        continue;
      }

      const c = cabecalho.colunas;
      const disciplina = celula(row, c.disciplina);
      const servico = celula(row, c.servico);
      const item = celula(row, c.item);
      const codigo = celula(row, c.codigo);
      const tipoEscrito = celula(row, c.tipo);
      const unidadeDoItem = celula(row, c.unidadeDoItem);
      const indiceEscrito = celula(row, c.indice);
      const perdaEscrita = celula(row, c.perda);

      const doItem = [codigo, tipoEscrito, unidadeDoItem, indiceEscrito, perdaEscrita];
      if (item === '') {
        // A linha que o modelo traz para o serviço que ainda não tem item: é um convite, e
        // não um erro. Só avisa quando alguém escreveu dado de item sem dizer de qual.
        if (doItem.some((valor) => valor !== '')) {
          avisar(
            linha,
            'A linha tem código, tipo, unidade, índice ou perda, mas não tem o nome do item.',
          );
        }
        continue;
      }
      if (disciplina === '' || servico === '') {
        avisar(linha, `O item "${item}" está sem ${disciplina === '' ? 'disciplina' : 'serviço'}.`);
        continue;
      }

      let tipo: ItemKind | undefined;
      if (tipoEscrito !== '') {
        tipo = TIPOS_DE_ITEM.get(simplificar(tipoEscrito));
        if (tipo === undefined) {
          avisar(
            linha,
            `O tipo "${tipoEscrito}" de ${item} não é reconhecido (material, insumo, ferramenta, equipamento ou mão de obra); a classe foi deixada como estava.`,
          );
        }
      }

      let indice: number | undefined;
      if (indiceEscrito !== '') {
        const numero = parseNumber(indiceEscrito);
        if (numero === undefined) {
          avisar(
            linha,
            `O índice "${indiceEscrito}" de ${item} não é um número e foi ignorado: o item entra sem índice (o que já estava gravado não muda).`,
          );
        } else if (numero < 0) {
          avisar(
            linha,
            `O índice de ${item} (${indiceEscrito}) não pode ser negativo e foi ignorado: o item entra sem índice (o que já estava gravado não muda).`,
          );
        } else if (numero > TETO_DO_INDICE) {
          avisar(
            linha,
            `O índice de ${item} (${indiceEscrito}) passa de ${TETO_DO_INDICE.toLocaleString('pt-BR')} e foi ignorado: o item entra sem índice (o que já estava gravado não muda).`,
          );
        } else {
          const gravado = arredondar(numero, CASAS_DO_INDICE);
          if (gravado !== numero) {
            avisar(
              linha,
              `O índice de ${item} (${indiceEscrito}) tem mais de ${String(CASAS_DO_INDICE)} casas decimais: ${gravado === 0 ? 'arredonda para zero e entra sem índice' : `foi gravado como ${gravado.toLocaleString('pt-BR', { maximumFractionDigits: CASAS_DO_INDICE })}`}.`,
            );
          }
          if (gravado > 0) indice = gravado;
        }
      }

      let perda: number | undefined;
      if (perdaEscrita !== '') {
        // "5%" é como quem digita escreve; o número é 5.
        const numero = parseNumber(perdaEscrita.replace('%', ''));
        if (numero === undefined) {
          avisar(
            linha,
            `A perda "${perdaEscrita}" de ${item} não é um número e foi ignorada: o item entra sem perda (o que já estava gravado não muda).`,
          );
        } else if (numero < 0) {
          avisar(
            linha,
            `A perda de ${item} (${perdaEscrita}) não pode ser negativa e foi ignorada: o item entra sem perda (o que já estava gravado não muda).`,
          );
        } else if (numero > TETO_DA_PERDA) {
          avisar(
            linha,
            `A perda de ${item} (${perdaEscrita}) deve ficar entre 0% e 1000% e foi ignorada: o item entra sem perda (o que já estava gravado não muda).`,
          );
        } else {
          const gravada = arredondar(numero, CASAS_DA_PERDA);
          if (gravada !== numero) {
            avisar(
              linha,
              `A perda de ${item} (${perdaEscrita}) tem mais de ${String(CASAS_DA_PERDA)} casas decimais: foi gravada como ${gravada.toLocaleString('pt-BR', { maximumFractionDigits: CASAS_DA_PERDA })}%.`,
            );
          }
          perda = gravada;
        }
      }

      indices.push({
        aba: aba.name,
        linha,
        disciplina,
        servico,
        item,
        ...opcional('etapa', celula(row, c.etapa)),
        ...opcional('unidadeDoServico', celula(row, c.unidadeDoServico)),
        ...opcional('medePor', celula(row, c.medePor)),
        ...opcional('codigo', codigo),
        ...opcional('unidadeDoItem', unidadeDoItem),
        ...(tipo === undefined ? {} : { tipo }),
        ...(indice === undefined ? {} : { indice }),
        ...(perda === undefined ? {} : { perda }),
      });
      aproveitadas += 1;
    }

    lidas.push({ name: aba.name, tipo: cabecalho.tipo, linhas: aproveitadas });
  }

  return { abas: lidas, indices, taxas, tiposDeEstaca, estacas, avisos };
}

// ── A mescla ────────────────────────────────────────────────────────────────────

export interface ResumoDaMescla {
  /** Itens que já existiam e ganharam, ou mudaram, o índice. */
  readonly indicesAtualizados: number;
  /** Itens que já existiam e mudaram em qualquer campo — índice, perda, unidade, classe… */
  readonly itensAtualizados: number;
  readonly itensNovos: number;
  /** Itens do CORTE E DOBRA que ganharam bitola, novos ou não (RN-366). */
  readonly itensComBitola: number;
  /** Taxas de aço que a planilha trouxe e a mescla aceitou. */
  readonly taxasGravadas: number;
  /** O que a planilha escreveu e a obra não tem — disciplina ou serviço —, sem repetir. */
  readonly naoCasados: readonly string[];
  /** Todos os avisos — os da leitura e os da mescla —, na ordem da planilha. */
  readonly avisos: readonly AvisoDaPlanilha[];
}

export interface MesclaDaPlanilha {
  readonly catalogo: ServiceCatalog;
  readonly resumo: ResumoDaMescla;
}

type Mutavel<T> = { -readonly [K in keyof T]: T[K] };

/**
 * Os avisos na ordem da planilha: a aba como ela veio na pasta e, dentro dela, a linha.
 *
 * Exportada porque a tela junta os avisos de duas mesclas — a do catálogo e a das estacas — e
 * a tabela única tem de continuar na ordem em que o usuário percorre a pasta.
 */
export function ordenarAvisos(
  abas: readonly AbaLida[],
  avisos: readonly AvisoDaPlanilha[],
): readonly AvisoDaPlanilha[] {
  const posicaoDaAba = new Map(abas.map((aba, indice) => [aba.name, indice]));
  return [...avisos].sort(
    (a, b) =>
      (posicaoDaAba.get(a.aba) ?? 0) - (posicaoDaAba.get(b.aba) ?? 0) ||
      (a.linha ?? 0) - (b.linha ?? 0),
  );
}

/** Sem acento, para o segundo olhar: a planilha digitada a mão esquece o cedilha. */
function semAcento(valor: string): string {
  return normalizeName(valor).normalize('NFKD').replace(/[̀-ͯ]/g, '');
}

/**
 * O nome casado dentro de uma lista: exato primeiro, sem acento depois — e só se for único.
 *
 * Exato primeiro porque a regra da casa é preservar o acento (`normalizeName`): dois serviços
 * que só diferem nele continuam sendo dois. O segundo olhar existe para o nome que a pessoa
 * escreveu sem o acento, e só vale quando não há dúvida de qual é.
 */
function casar<T extends { readonly name: string }>(
  candidatos: readonly T[],
  texto: string,
): T | undefined {
  const exato = candidatos.find(
    (candidato) => normalizeName(candidato.name) === normalizeName(texto),
  );
  if (exato !== undefined) return exato;
  const parecidos = candidatos.filter(
    (candidato) => semAcento(candidato.name) === semAcento(texto),
  );
  return parecidos.length === 1 ? parecidos[0] : undefined;
}

interface DisciplinaEmTrabalho {
  readonly original?: CatalogDiscipline;
  readonly key: string;
  readonly name: string;
  readonly services: CatalogService[];
  steelRates?: readonly SteelRate[];
  mexida: boolean;
}

/**
 * A planilha plana entrando no catálogo da obra.
 *
 * As regras, na ordem em que a linha as encontra:
 *
 * 1. **O serviço tem de existir** (no cadastro da obra ou no catálogo): disciplina ou serviço
 *    que não casa vira aviso — "não casou" —, e nada é criado. Uma linha para cada serviço
 *    ausente seria uma tela de aviso; o aviso é um só, com quantas linhas ele cobre.
 * 2. **O aço só se compra no CORTE E DOBRA** (RN-366). Item com bitola na descrição, em
 *    qualquer outro serviço, não entra: a MONTAGEM e a INSTALAÇÃO contam o mesmo kg, mas como
 *    quantidade do serviço. No corte e dobra o item ganha a bitola e o **índice é ignorado**
 *    — a quantidade dele virá da taxa da disciplina (RN-378).
 * 3. **Mescla, nunca remove.** O item casa pelo código de compras e, sem código, pela
 *    descrição normalizada; o que casa tem índice, perda, unidade e classe atualizados, e o
 *    que não casa é criado. O item do serviço que a planilha não cita continua onde estava.
 * 4. **A célula vazia não apaga.** Índice vazio ou 0 é "não preenchido" (D14): num item novo
 *    ele nasce **sem índice**, e num item que já tem índice, não o toca. Apagar o que alguém
 *    digitou na tela por um buraco na planilha — que pode ser só uma planilha que não o
 *    trazia — é o tipo de perda que ninguém percebe até a compra sair errada. Para zerar o
 *    índice, a tela do serviço.
 *
 * O catálogo devolvido é **inteiro** — `IMPORT_CATALOG` troca o catálogo, e devolver só o que a
 * planilha tocou apagaria o resto. A origem (`fileName`, `sources`) fica como estava: a
 * planilha plana não é uma importação do orçamento.
 */
export function mesclarNoCatalogo(
  catalogo: ServiceCatalog | undefined,
  disponiveis: readonly ServicoDisponivel[],
  leitura: LeituraDaPlanilha,
  desativados: readonly ServicoDesativado[] = [],
): MesclaDaPlanilha {
  const base = catalogo ?? emptyCatalog();
  const conhecidas = listarDisciplinas(base, disponiveis, desativados);
  // Com os desativados dentro: é onde se olha para dizer "desativado" em vez de "não existe".
  const todas = desativados.length === 0 ? conhecidas : listarDisciplinas(base, disponiveis);

  const emTrabalho = new Map<string, DisciplinaEmTrabalho>();
  const ordem: DisciplinaEmTrabalho[] = base.disciplines.map((disciplina) => {
    const trabalho: DisciplinaEmTrabalho = {
      original: disciplina,
      key: disciplina.key,
      name: disciplina.name,
      services: [...disciplina.services],
      ...(disciplina.steelRates === undefined ? {} : { steelRates: disciplina.steelRates }),
      mexida: false,
    };
    emTrabalho.set(normalizeName(disciplina.name), trabalho);
    return trabalho;
  });

  /** A disciplina do catálogo, criada na hora quando só o cadastro da obra a conhece. */
  const garantirDisciplina = (disciplina: DisciplinaListada): DisciplinaEmTrabalho => {
    const chave = normalizeName(disciplina.name);
    let trabalho = emTrabalho.get(chave);
    if (trabalho === undefined) {
      trabalho = { key: chave, name: disciplina.name, services: [], mexida: true };
      emTrabalho.set(chave, trabalho);
      ordem.push(trabalho);
    }
    return trabalho;
  };

  const garantirServico = (
    disciplina: DisciplinaListada,
    servico: ServicoListado,
  ): { readonly trabalho: DisciplinaEmTrabalho; readonly indice: number } => {
    const trabalho = garantirDisciplina(disciplina);
    let indice = trabalho.services.findIndex(
      (existente) => normalizeName(existente.name) === normalizeName(servico.name),
    );
    if (indice < 0) {
      // Mesma forma de `paraCatalogo`: o que o cadastro grava é decisão confirmada.
      trabalho.services.push({
        key: serviceKey(trabalho.name, servico.name),
        name: servico.name,
        measureBy: servico.measureBy,
        measureOrigin: 'DECIDIDO',
        items: [],
        quantityBySheet: {},
        ...(servico.appliesTo === undefined ? {} : { appliesTo: servico.appliesTo }),
        ...(servico.unit === undefined ? {} : { unit: servico.unit }),
      });
      indice = trabalho.services.length - 1;
      trabalho.mexida = true;
    }
    return { trabalho, indice };
  };

  const avisos: AvisoDaPlanilha[] = [];
  /** O que não casou, agrupado: o serviço ausente é um aviso, com quantas linhas ele cobre. */
  const ausentes = new Map<
    string,
    {
      aba: string;
      linha: number;
      rotulo: string;
      mensagem: string;
      linhas: number;
      desativado: boolean;
    }
  >();
  const registrarAusente = (
    aba: string,
    linha: number,
    chave: string,
    rotulo: string,
    mensagem: string,
    desativado = false,
  ): void => {
    const atual = ausentes.get(chave);
    if (atual === undefined) {
      ausentes.set(chave, { aba, linha, rotulo, mensagem, linhas: 1, desativado });
    } else atual.linhas += 1;
  };

  /**
   * O nome que não casou entre os serviços ativos, mas casa com um desativado: o aviso diz isso,
   * e é outro aviso — "não existe" mandaria a pessoa corrigir o nome que já estava certo.
   * Devolve `true` quando era caso de desativado (e já foi registrado).
   */
  const registrarSeDesativado = (
    aba: string,
    linha: number,
    disciplinaEscrita: string,
    servicoEscrito: string | undefined,
    tipoDaChave: string,
  ): boolean => {
    if (desativados.length === 0) return false;
    const daTodas = casar(todas, disciplinaEscrita);
    if (daTodas === undefined) return false;
    const chaveDaDisciplina = `${aba}|${tipoDaChave}|desativado|${normalizeName(daTodas.name)}`;
    const ativa = casar(conhecidas, disciplinaEscrita);
    if (ativa === undefined) {
      registrarAusente(
        aba,
        linha,
        chaveDaDisciplina,
        daTodas.name,
        `Serviço desativado: a disciplina "${daTodas.name}" só tem serviços desativados nesta obra, e nada foi criado nela.`,
        true,
      );
      return true;
    }
    if (servicoEscrito === undefined) return false;
    const doServico = casar(daTodas.services, servicoEscrito);
    if (doServico === undefined || casar(ativa.services, servicoEscrito) !== undefined) {
      return false;
    }
    registrarAusente(
      aba,
      linha,
      `${chaveDaDisciplina}|${normalizeName(doServico.name)}`,
      `${daTodas.name} > ${doServico.name}`,
      `Serviço desativado: "${doServico.name}" (${daTodas.name}) está desativado nesta obra, e nenhum item foi criado nele.`,
      true,
    );
    return true;
  };

  let indicesAtualizados = 0;
  let itensAtualizados = 0;
  let itensNovos = 0;
  let itensComBitola = 0;
  let taxasGravadas = 0;
  /** O item já tocado nesta importação: a segunda linha dele é aviso, e não vence a primeira. */
  const tocados = new Set<string>();

  for (const linha of leitura.indices) {
    const avisar = (mensagem: string): void => {
      avisos.push({ aba: linha.aba, linha: linha.linha, mensagem });
    };

    const disciplina = casar(conhecidas, linha.disciplina);
    if (disciplina === undefined) {
      if (registrarSeDesativado(linha.aba, linha.linha, linha.disciplina, linha.servico, 'i')) {
        continue;
      }
      registrarAusente(
        linha.aba,
        linha.linha,
        `${linha.aba}|${normalizeName(linha.disciplina)}`,
        linha.disciplina,
        `Não casou: a disciplina "${linha.disciplina}" não existe nesta obra.`,
      );
      continue;
    }
    const servico = casar(disciplina.services, linha.servico);
    if (servico === undefined) {
      if (registrarSeDesativado(linha.aba, linha.linha, linha.disciplina, linha.servico, 'i')) {
        continue;
      }
      registrarAusente(
        linha.aba,
        linha.linha,
        `${linha.aba}|${normalizeName(linha.disciplina)}|${normalizeName(linha.servico)}`,
        `${disciplina.name} > ${linha.servico}`,
        `Não casou: o serviço "${linha.servico}" não existe na disciplina "${disciplina.name}" desta obra.`,
      );
      continue;
    }

    const bitola = parseSteelGauge(linha.item);
    const ehCorteEDobra = isSteelPurchaseService(servico.name);
    if (bitola !== undefined && !ehCorteEDobra) {
      avisar(
        `${linha.item} tem bitola e não entra em ${servico.name} (${disciplina.name}): o aço só se compra no ${STEEL_PURCHASE_SERVICE} (RN-366). Nada foi criado.`,
      );
      continue;
    }

    const { trabalho, indice: posicao } = garantirServico(disciplina, servico);
    const atual = trabalho.services[posicao]!;
    const chaveDoServico = `${normalizeName(disciplina.name)}|${normalizeName(servico.name)}`;

    // O código manda quando a linha tem um; a descrição resolve o resto (F11).
    const casado =
      (linha.codigo === undefined
        ? undefined
        : atual.items.find(
            (item) =>
              item.code !== undefined && normalizeName(item.code) === normalizeName(linha.codigo!),
          )) ??
      atual.items.find(
        (item) =>
          item.key === normalizeName(linha.item) ||
          normalizeName(item.description) === normalizeName(linha.item),
      );

    if (casado !== undefined && tocados.has(`${chaveDoServico}»${casado.key}`)) {
      avisar(
        `${linha.item} aparece mais de uma vez em ${servico.name}; vale a primeira ocorrência, e esta foi ignorada.`,
      );
      continue;
    }

    // O aço do corte e dobra: o índice não conta, e a planilha que o preencheu é avisada. O item
    // que já tem bitola é aço mesmo quando a descrição da linha não a escreve (casou pelo código).
    const ehAco = bitola !== undefined || (casado?.steelGauge !== undefined && ehCorteEDobra);
    if (ehAco && linha.indice !== undefined) {
      avisar(
        `O índice de ${linha.item} foi ignorado: no ${STEEL_PURCHASE_SERVICE} a quantidade vem da taxa de aço da disciplina.`,
      );
    }
    const indiceAplicavel = ehAco ? undefined : linha.indice;

    if (casado === undefined) {
      const descricao = itemDescription(linha.item);
      const kind =
        linha.tipo ??
        guessItemKind({
          disciplineName: disciplina.name,
          serviceName: servico.name,
          description: descricao,
          ...(linha.unidadeDoItem === undefined ? {} : { unit: linha.unidadeDoItem }),
        });
      if (kind === 'MAO_DE_OBRA' && linha.perda !== undefined && linha.perda > 0) {
        avisar(`A perda de ${descricao} foi ignorada: hora de gente não se perde no caminho.`);
      }
      const novo: CatalogItem = {
        key: normalizeName(descricao),
        description: descricao,
        quantityBySheet: {},
        kind,
        kindOrigin: linha.tipo === undefined ? 'PALPITE' : 'DECIDIDO',
        ...(linha.codigo === undefined ? {} : { code: linha.codigo }),
        ...(linha.unidadeDoItem === undefined ? {} : { unit: linha.unidadeDoItem }),
        ...(bitola === undefined ? {} : { steelGauge: bitola }),
        // O índice da planilha é de quem a preencheu (Fase 40, RN-520): digitado.
        ...(indiceAplicavel === undefined
          ? {}
          : { consumptionIndex: indiceAplicavel, indexOrigin: 'DIGITADO' as const }),
        ...(linha.perda === undefined || kind === 'MAO_DE_OBRA'
          ? {}
          : { lossPercent: linha.perda }),
      };
      trabalho.services[posicao] = { ...atual, items: [...atual.items, novo] };
      trabalho.mexida = true;
      tocados.add(`${chaveDoServico}»${novo.key}`);
      itensNovos += 1;
      if (bitola !== undefined) itensComBitola += 1;
      continue;
    }

    // O item que já existe: só muda o que a planilha **disse**.
    const novo: Mutavel<CatalogItem> = { ...casado };
    let mudou = false;
    let indiceMudou = false;

    // Só a classe **diferente** é decisão. O modelo exporta o tipo de todo item, e quem o devolve
    // sem mexer não decidiu nada: marcar tudo como decidido congelaria os palpites que ainda
    // melhoram na reimportação do orçamento (`GuessOrigin`).
    if (linha.tipo !== undefined && linha.tipo !== casado.kind) {
      novo.kind = linha.tipo;
      novo.kindOrigin = 'DECIDIDO';
      mudou = true;
      // A função é da mão de obra, e a perda não é: trocar a classe leva uma e traz a outra.
      if (linha.tipo !== 'MAO_DE_OBRA') delete novo.jobRoleId;
      else delete novo.lossPercent;
    }
    if (linha.unidadeDoItem !== undefined && linha.unidadeDoItem !== casado.unit) {
      novo.unit = linha.unidadeDoItem;
      mudou = true;
    }
    // O código que já existia vence o que chega — `mergeItem` faz o mesmo na reimportação.
    if (linha.codigo !== undefined && casado.code === undefined) {
      novo.code = linha.codigo;
      mudou = true;
    }
    if (bitola !== undefined && casado.steelGauge !== bitola) {
      novo.steelGauge = bitola;
      mudou = true;
      itensComBitola += 1;
    }
    if (indiceAplicavel !== undefined && indiceAplicavel !== casado.consumptionIndex) {
      novo.consumptionIndex = indiceAplicavel;
      // O número que a planilha trouxe é de alguém (Fase 40, RN-520): a média fica ao lado.
      novo.indexOrigin = 'DIGITADO';
      mudou = true;
      indiceMudou = true;
    }
    if (linha.perda !== undefined) {
      if (novo.kind === 'MAO_DE_OBRA') {
        if (linha.perda > 0) {
          avisar(
            `A perda de ${casado.description} foi ignorada: hora de gente não se perde no caminho.`,
          );
        }
      } else if (linha.perda !== casado.lossPercent) {
        novo.lossPercent = linha.perda;
        mudou = true;
      }
    }

    tocados.add(`${chaveDoServico}»${casado.key}`);
    if (!mudou) continue;
    trabalho.services[posicao] = {
      ...atual,
      items: atual.items.map((item) => (item === casado ? novo : item)),
    };
    trabalho.mexida = true;
    itensAtualizados += 1;
    if (indiceMudou) indicesAtualizados += 1;
  }

  // ── As taxas de aço ───────────────────────────────────────────────────────────

  const aceitasPorDisciplina = new Map<
    string,
    { readonly disciplina: DisciplinaListada; readonly aceitas: SteelRate[] }
  >();
  for (const taxa of leitura.taxas) {
    const disciplina = casar(conhecidas, taxa.disciplina);
    if (disciplina === undefined) {
      if (registrarSeDesativado(taxa.aba, taxa.linha, taxa.disciplina, undefined, 't')) continue;
      registrarAusente(
        taxa.aba,
        taxa.linha,
        `${taxa.aba}|taxa|${normalizeName(taxa.disciplina)}`,
        taxa.disciplina,
        `Não casou: a disciplina "${taxa.disciplina}" não existe nesta obra, e a taxa de aço dela não foi gravada.`,
      );
      continue;
    }
    const chave = normalizeName(disciplina.name);
    const achada = aceitasPorDisciplina.get(chave) ?? { disciplina, aceitas: [] };
    // A conferência é a do domínio — positiva, uma linha por bitola —, aplicada linha a linha
    // para que o aviso diga qual: a segunda linha da mesma bitola é a recusada.
    const candidata: SteelRate = { gauge: taxa.gauge, kgPerM3: taxa.kgPerM3 };
    const conferida = normalizeSteelRates([...achada.aceitas, candidata]);
    if (!conferida.ok) {
      avisos.push({ aba: taxa.aba, linha: taxa.linha, mensagem: conferida.error.message });
      continue;
    }
    achada.aceitas.push(candidata);
    aceitasPorDisciplina.set(chave, achada);
  }

  for (const [chave, { disciplina, aceitas }] of aceitasPorDisciplina) {
    const existente = emTrabalho.get(chave);
    const atuais = existente?.steelRates ?? disciplina.steelRates;
    // Só a taxa que **muda** é mudança: a ida e volta pura devolve as mesmas, e contá-las faria
    // a prévia oferecer "aplicar" para o que não muda nada.
    const quemMuda = aceitas.filter(
      (nova) =>
        !atuais.some((antiga) => antiga.gauge === nova.gauge && antiga.kgPerM3 === nova.kgPerM3),
    );
    if (quemMuda.length === 0) continue;

    // A bitola que a planilha não cita continua com a taxa que tinha: mescla, não troca.
    const mantidas = atuais.filter(
      (antiga) => !aceitas.some((nova) => nova.gauge === antiga.gauge),
    );
    const final = normalizeSteelRates([...mantidas, ...aceitas]);
    if (!final.ok) {
      avisos.push({
        aba: leitura.taxas[0]?.aba ?? '',
        mensagem: `${disciplina.name}: ${final.error.message} A taxa dessa disciplina não foi gravada.`,
      });
      continue;
    }
    const trabalho = existente ?? garantirDisciplina(disciplina);
    trabalho.steelRates = final.value;
    trabalho.mexida = true;
    taxasGravadas += quemMuda.length;
  }

  for (const ausente of ausentes.values()) {
    avisos.push({
      aba: ausente.aba,
      linha: ausente.linha,
      mensagem:
        ausente.linhas === 1
          ? ausente.mensagem
          : `${ausente.mensagem} São ${String(ausente.linhas)} linhas, a primeira na linha ${String(ausente.linha)}.`,
    });
  }

  const todosOsAvisos = ordenarAvisos(leitura.abas, [...leitura.avisos, ...avisos]);

  const disciplinas = ordem.map((trabalho): CatalogDiscipline => {
    if (!trabalho.mexida && trabalho.original !== undefined) return trabalho.original;
    const { steelRates: _anteriores, ...semTaxas } = trabalho.original ?? {
      key: trabalho.key,
      name: trabalho.name,
      services: [],
    };
    return {
      ...semTaxas,
      services: trabalho.services,
      ...(trabalho.steelRates === undefined || trabalho.steelRates.length === 0
        ? {}
        : { steelRates: trabalho.steelRates }),
    };
  });

  return {
    catalogo: { ...base, disciplines: disciplinas },
    resumo: {
      indicesAtualizados,
      itensAtualizados,
      itensNovos,
      itensComBitola,
      taxasGravadas,
      naoCasados: [
        ...new Set(
          [...ausentes.values()].filter((a) => !a.desativado).map((ausente) => ausente.rotulo),
        ),
      ],
      avisos: todosOsAvisos,
    },
  };
}

// ── Os tipos de estaca e as estacas ─────────────────────────────────────────────

/**
 * Um desenho que pode receber estacas: a tipologia de **área comum** do pavimento de Fundação ou
 * de Contenção (F10, RN-361).
 *
 * É a forma mínima que a mescla precisa conhecer — o rascunho da tela tem mais coisa, e o
 * domínio não depende dele. `pavimentos` são os nomes que o pavimento tem na estrutura da obra
 * ("Fundação", "Fundação Torre 2"), e servem para a coluna **Pavimento** casar por qualquer um
 * dos dois nomes; `existentes` são as estacas vivas que o desenho já tem (RN-376).
 */
export interface DestinoDeEstacas {
  readonly unitTypeId: UnitTypeId;
  readonly nome: string;
  readonly pavimentos: readonly string[];
  readonly existentes: readonly string[];
}

export interface ContextoDasEstacas {
  /** A obra, para os tipos novos. */
  readonly projectId: ProjectId;
  /** Os tipos que a obra já tem — o que a aba Tipos atualiza e a aba Estacas escolhe. */
  readonly pileTypes: readonly PileType[];
  readonly destinos: readonly DestinoDeEstacas[];
  /**
   * O "Pavimento das estacas" escolhido na tela. Ausente é "nada escolhido": só a coluna
   * Pavimento resolve a linha que a traz, e a outra é avisada.
   */
  readonly destinoPadrao?: UnitTypeId;
  /** O instante de criação e de correção dos tipos. De fora, para a mescla continuar pura. */
  readonly agora: string;
  readonly novoId: () => PileTypeId;
}

export interface EstacasParaImportar {
  readonly unitTypeId: UnitTypeId;
  readonly piles: readonly { readonly name: string; readonly pileTypeId: PileTypeId }[];
}

export interface ResumoDasEstacas {
  readonly tiposNovos: number;
  /** Tipos que já existiam e mudaram — diâmetro, comprimento ou aço. */
  readonly tiposAtualizados: number;
  /** Estacas que a confirmação cria, "a posicionar" (D5). */
  readonly estacasNovas: number;
  /** Só os avisos **desta** mescla: os da leitura já estão em `LeituraDaPlanilha.avisos`. */
  readonly avisos: readonly AvisoDaPlanilha[];
}

export interface MesclaDasEstacas {
  /** Os tipos da obra depois da planilha, para `SET_PILE_TYPES`. Os mesmos objetos, se nada mudou. */
  readonly pileTypes: readonly PileType[];
  /** Por tipologia de destino, para `IMPORT_PILES`. Só a que recebe alguma estaca. */
  readonly porTipologia: readonly EstacasParaImportar[];
  readonly resumo: ResumoDasEstacas;
}

function metros(valor: number): string {
  return valor.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
}

function mesmoValor(a: number, b: number): boolean {
  return Math.abs(a - b) < 0.0005;
}

function mesmoAco(a: readonly PileSteel[], b: readonly PileSteel[]): boolean {
  return (
    a.length === b.length &&
    a.every(
      (linha, i) => linha.gauge === b[i]!.gauge && mesmoValor(linha.kgPerPile, b[i]!.kgPerPile),
    )
  );
}

interface TipoLido {
  readonly nome: string;
  readonly primeiraLinha: LinhaDeTipoDeEstaca;
  diametro?: number;
  comprimento?: number;
  readonly aco: PileSteel[];
}

/**
 * O destino que o texto da coluna Pavimento escolhe: o nome da tipologia primeiro, o do
 * pavimento depois, sem acento e sem diferenciar caixa — e só se for **um** (F10).
 */
function acharDestino(
  destinos: readonly DestinoDeEstacas[],
  texto: string,
): DestinoDeEstacas | 'AMBIGUO' | undefined {
  const procurado = semAcento(texto);
  const porTipologia = destinos.filter((destino) => semAcento(destino.nome) === procurado);
  if (porTipologia.length > 0) return porTipologia.length === 1 ? porTipologia[0] : 'AMBIGUO';
  const porPavimento = destinos.filter((destino) =>
    destino.pavimentos.some((pavimento) => semAcento(pavimento) === procurado),
  );
  if (porPavimento.length > 0) return porPavimento.length === 1 ? porPavimento[0] : 'AMBIGUO';
  return undefined;
}

/**
 * A planilha de tipos e de estacas entrando na obra — **sem gravar nada**: devolve o que a
 * confirmação despacha (`SET_PILE_TYPES`, `IMPORT_PILES`) e o que ficou de fora.
 *
 * **Os tipos** casam pelo nome contra os da obra (mesma regra do serviço: exato primeiro, sem
 * acento depois, só se único):
 *
 * - o que casa tem diâmetro, comprimento e aço atualizados — a bitola que a planilha não cita
 *   continua no tipo, como a taxa que a planilha de taxas não cita (mescla, nunca remove);
 * - o que não casa é criado, e só com diâmetro **e** comprimento;
 * - toda mudança passa por `checkPileTypeFields`, o mesmo conferidor do cadastro (RN-362): o
 *   que ele recusa vira aviso com a linha, e o tipo fica como estava.
 *
 * Um tipo escrito em várias linhas (uma por bitola) tem de repetir o mesmo diâmetro e o mesmo
 * comprimento; o que discordar da primeira linha é avisado, e vale a primeira.
 *
 * **As estacas** entram "a posicionar" (D5), cada uma no pavimento que a linha diz — a coluna
 * Pavimento, pelo nome da tipologia ou do pavimento — ou, se a linha não diz, no que a tela
 * escolheu. A escolha escrita **vence**, e quando não casa não há plano B: mandar para o
 * pavimento da tela a estaca que a planilha pôs em outro seria acertar o que ninguém pediu.
 * Fica de fora, com aviso, a estaca de tipo que não existe (nem na obra, nem na aba Tipos), a
 * repetida na planilha (vale a primeira) e a que o pavimento já tem (RN-376).
 *
 * Sem nenhum pavimento de Fundação ou Contenção com área comum na obra, as estacas não entram
 * e o aviso diz o que fazer — "Desenhar este pavimento" —, mas os tipos entram do mesmo jeito:
 * cadastrar o tipo não depende de haver onde pôr a estaca.
 */
export function mesclarEstacas(
  leitura: LeituraDaPlanilha,
  contexto: ContextoDasEstacas,
): MesclaDasEstacas {
  const avisos: AvisoDaPlanilha[] = [];
  const { agora } = contexto;

  // ── Os tipos ──────────────────────────────────────────────────────────────────

  const lidos = new Map<string, TipoLido>();
  for (const linha of leitura.tiposDeEstaca) {
    const chave = normalizeName(linha.nome);
    let lido = lidos.get(chave);
    if (lido === undefined) {
      lido = { nome: linha.nome, primeiraLinha: linha, aco: [] };
      lidos.set(chave, lido);
    }
    if (linha.diametro !== undefined) {
      if (lido.diametro === undefined) lido.diametro = linha.diametro;
      else if (!mesmoValor(lido.diametro, linha.diametro)) {
        avisos.push({
          aba: linha.aba,
          linha: linha.linha,
          mensagem: `O diâmetro de ${lido.nome} (${metros(linha.diametro)} m) difere do da primeira linha (${metros(lido.diametro)} m); vale o primeiro.`,
        });
      }
    }
    if (linha.comprimento !== undefined) {
      if (lido.comprimento === undefined) lido.comprimento = linha.comprimento;
      else if (!mesmoValor(lido.comprimento, linha.comprimento)) {
        avisos.push({
          aba: linha.aba,
          linha: linha.linha,
          mensagem: `O comprimento de ${lido.nome} (${metros(linha.comprimento)} m) difere do da primeira linha (${metros(lido.comprimento)} m); vale o primeiro.`,
        });
      }
    }
    if (linha.aco !== undefined) {
      const bitola = linha.aco.gauge;
      if (lido.aco.some((existente) => existente.gauge === bitola)) {
        avisos.push({
          aba: linha.aba,
          linha: linha.linha,
          mensagem: `A bitola ${steelGaugeLabel(bitola)} aparece mais de uma vez no tipo ${lido.nome}; vale a primeira, e esta foi ignorada.`,
        });
      } else {
        lido.aco.push(linha.aco);
      }
    }
  }

  let tipos: readonly PileType[] = contexto.pileTypes;
  let tiposNovos = 0;
  let tiposAtualizados = 0;
  const vivos = (): readonly PileType[] => tipos.filter((tipo) => tipo.deletedAt === undefined);

  for (const lido of lidos.values()) {
    const avisar = (mensagem: string): void => {
      avisos.push({ aba: lido.primeiraLinha.aba, linha: lido.primeiraLinha.linha, mensagem });
    };
    const existente = casar(vivos(), lido.nome);

    if (existente !== undefined) {
      const conferido = checkPileTypeFields(
        {
          name: existente.name,
          diameter: lido.diametro ?? existente.diameter,
          length: lido.comprimento ?? existente.length,
          // A bitola citada troca a do tipo; a não citada fica.
          steel: [
            ...existente.steel.filter(
              (antiga) => !lido.aco.some((nova) => nova.gauge === antiga.gauge),
            ),
            ...lido.aco,
          ],
        },
        tipos,
        existente.id,
      );
      if (!conferido.ok) {
        avisar(`${conferido.error.message} O tipo ${existente.name} ficou como estava.`);
        continue;
      }
      const campos = conferido.value;
      if (
        campos.diameter === existente.diameter &&
        campos.length === existente.length &&
        mesmoAco(campos.steel, existente.steel)
      ) {
        continue;
      }
      tipos = tipos.map((tipo) =>
        tipo.id === existente.id
          ? {
              ...tipo,
              diameter: campos.diameter,
              length: campos.length,
              steel: campos.steel,
              updatedAt: agora,
            }
          : tipo,
      );
      tiposAtualizados += 1;
      continue;
    }

    if (lido.diametro === undefined || lido.comprimento === undefined) {
      const falta =
        lido.diametro === undefined && lido.comprimento === undefined
          ? 'diâmetro e comprimento'
          : lido.diametro === undefined
            ? 'diâmetro'
            : 'comprimento';
      avisar(`O tipo ${lido.nome} é novo e está sem ${falta}; ele não foi criado.`);
      continue;
    }
    const conferido = checkPileTypeFields(
      { name: lido.nome, diameter: lido.diametro, length: lido.comprimento, steel: lido.aco },
      tipos,
    );
    if (!conferido.ok) {
      avisar(`${conferido.error.message} O tipo ${lido.nome} não foi criado.`);
      continue;
    }
    const campos = conferido.value;
    tipos = [
      ...tipos,
      {
        id: contexto.novoId(),
        projectId: contexto.projectId,
        name: campos.name,
        diameter: campos.diameter,
        length: campos.length,
        steel: campos.steel,
        createdAt: agora,
        updatedAt: agora,
      },
    ];
    tiposNovos += 1;
  }

  // ── As estacas ────────────────────────────────────────────────────────────────

  const destinos = contexto.destinos;
  const padrao = destinos.find((destino) => destino.unitTypeId === contexto.destinoPadrao);
  const recebidas = new Map<UnitTypeId, { name: string; pileTypeId: PileTypeId }[]>();
  let estacasNovas = 0;

  /** O que não casou, agrupado: cinquenta estacas do mesmo tipo ausente são um aviso. */
  const agrupados = new Map<
    string,
    {
      readonly aba: string;
      readonly linha: number;
      linhas: number;
      readonly uma: string;
      readonly muitas: (linhas: number, primeira: number) => string;
    }
  >();
  const agrupar = (
    chave: string,
    estaca: LinhaDeEstaca,
    uma: string,
    muitas: (linhas: number, primeira: number) => string,
  ): void => {
    const atual = agrupados.get(chave);
    if (atual === undefined) {
      agrupados.set(chave, { aba: estaca.aba, linha: estaca.linha, linhas: 1, uma, muitas });
    } else {
      atual.linhas += 1;
    }
  };

  if (leitura.estacas.length > 0 && destinos.length === 0) {
    const primeira = leitura.estacas[0]!;
    const varias = leitura.estacas.length > 1;
    avisos.push({
      aba: primeira.aba,
      linha: primeira.linha,
      mensagem: `A obra não tem pavimento de Fundação ou Contenção com área comum: use "Desenhar este pavimento" primeiro. ${
        varias ? `As ${String(leitura.estacas.length)} estacas` : 'A estaca'
      } da planilha ${varias ? 'não foram importadas' : 'não foi importada'}.`,
    });
  } else {
    const vistas = new Set<string>();
    const jaExistentes = new Map<UnitTypeId, Set<string>>();
    const existentesDe = (destino: DestinoDeEstacas): Set<string> => {
      let nomes = jaExistentes.get(destino.unitTypeId);
      if (nomes === undefined) {
        nomes = new Set(destino.existentes.map(normalizeName));
        jaExistentes.set(destino.unitTypeId, nomes);
      }
      return nomes;
    };

    for (const estaca of leitura.estacas) {
      const tipo = casar(vivos(), estaca.tipo);
      if (tipo === undefined) {
        agrupar(
          `tipo|${normalizeName(estaca.tipo)}`,
          estaca,
          `O tipo "${estaca.tipo}" da estaca ${estaca.nome} não existe nesta obra nem na aba Tipos; ela não foi importada.`,
          (linhas, primeira) =>
            `O tipo "${estaca.tipo}" não existe nesta obra nem na aba Tipos; ${String(linhas)} estacas não foram importadas, a primeira na linha ${String(primeira)}.`,
        );
        continue;
      }

      let destino: DestinoDeEstacas;
      if (estaca.pavimento === undefined) {
        if (padrao === undefined) {
          agrupar(
            'sem-pavimento',
            estaca,
            `A estaca ${estaca.nome} ficou sem pavimento: escolha o "Pavimento das estacas" ou preencha a coluna Pavimento.`,
            (linhas, primeira) =>
              `${String(linhas)} estacas ficaram sem pavimento, a primeira na linha ${String(primeira)}: escolha o "Pavimento das estacas" ou preencha a coluna Pavimento.`,
          );
          continue;
        }
        destino = padrao;
      } else {
        const escrito = estaca.pavimento;
        const achado = acharDestino(destinos, escrito);
        if (achado === undefined || achado === 'AMBIGUO') {
          const motivo =
            achado === undefined
              ? 'não casa com nenhuma tipologia de área comum de Fundação ou Contenção'
              : 'casa com mais de uma tipologia; escreva o nome da tipologia';
          agrupar(
            `pavimento|${normalizeName(escrito)}`,
            estaca,
            `O pavimento "${escrito}" da estaca ${estaca.nome} ${motivo}; ela não foi importada.`,
            (linhas, primeira) =>
              `O pavimento "${escrito}" ${motivo}; ${String(linhas)} estacas não foram importadas, a primeira na linha ${String(primeira)}.`,
          );
          continue;
        }
        destino = achado;
      }

      const chave = normalizeName(estaca.nome);
      const daPlanilha = `${destino.unitTypeId}|${chave}`;
      if (vistas.has(daPlanilha)) {
        avisos.push({
          aba: estaca.aba,
          linha: estaca.linha,
          mensagem: `A estaca ${estaca.nome} aparece mais de uma vez em ${destino.nome}; vale a primeira ocorrência, e esta foi ignorada.`,
        });
        continue;
      }
      vistas.add(daPlanilha);

      if (existentesDe(destino).has(chave)) {
        const onde = destino.nome;
        agrupar(
          `existe|${destino.unitTypeId}`,
          estaca,
          `A estaca ${estaca.nome} já existe em ${onde} e não foi duplicada.`,
          (linhas, primeira) =>
            `${String(linhas)} estacas já existem em ${onde} e não foram duplicadas, a primeira na linha ${String(primeira)}.`,
        );
        continue;
      }

      const lista = recebidas.get(destino.unitTypeId) ?? [];
      lista.push({ name: estaca.nome, pileTypeId: tipo.id });
      recebidas.set(destino.unitTypeId, lista);
      estacasNovas += 1;
    }
  }

  for (const agrupado of agrupados.values()) {
    avisos.push({
      aba: agrupado.aba,
      linha: agrupado.linha,
      mensagem:
        agrupado.linhas === 1 ? agrupado.uma : agrupado.muitas(agrupado.linhas, agrupado.linha),
    });
  }

  return {
    pileTypes: tipos,
    porTipologia: [...recebidas].map(([unitTypeId, piles]) => ({ unitTypeId, piles })),
    resumo: {
      tiposNovos,
      tiposAtualizados,
      estacasNovas,
      avisos: ordenarAvisos(leitura.abas, avisos),
    },
  };
}
